"use server"

import { sdk } from "@lib/config"
import { getAuthHeaders } from "@lib/data/cookies"
import {
  parseApplicationFormFields,
  type ApplicationFormField,
  type ApplicationFormValues,
} from "@modules/affiliate-application/lib/application-form-model"

// Story 10.6 — a camada de dados da candidatura a afiliado. `"use server"`: o browser NUNCA fala com o
// Engine direto (o SDK é server-side e anexa a publishable key sozinho — nunca a pôr à mão). Resultado
// DISCRIMINADO (`{ok:true}|{ok:false,error}`) porque um server action NUNCA lança ao componente: o
// formulário tem de mostrar um erro, não desaparecer num boundary.
//
// A rota `/store/affiliate-applications` é PÚBLICA (não registada em `middlewares.ts`), logo nada aqui
// exige sessão. Ainda assim ANEXAMOS `getAuthHeaders()`: para um visitante devolve `{}` (caso normal),
// e para um cliente com sessão leva o bearer que faz o Engine gravar `customer_id = actor_id`. Sem
// isto, o `customer_id` nunca seria preenchido a partir do produto. O header é opcional — nunca um
// requisito.

const APPLICATION_FORM_PATH = "/store/affiliate-application-form"
const APPLICATIONS_PATH = "/store/affiliate-applications"

const HTTP_BAD_REQUEST = 400

const FIELDS_ERROR_MESSAGE =
  "Não foi possível carregar o formulário agora. Atualize a página em instantes."
const SUBMIT_ERROR_MESSAGE =
  "Não foi possível registrar sua candidatura agora. Tente novamente em instantes."

export type AffiliateApplicationFieldsResult =
  | { ok: true; fields: ApplicationFormField[] }
  | { ok: false; error: string }

export type SubmitAffiliateApplicationResult =
  | { ok: true; id: string }
  | { ok: false; error: string }

/**
 * Lê a definição dos campos do Engine. É o que torna a página `/novos-afiliados` uma renderização DA
 * CONFIGURAÇÃO — os campos não estão escritos no JSX.
 *
 * `no-store`: a definição é servida de uma constante do Engine e muda com um deploy, não com o tempo;
 * cachear aqui só criaria um estado em que a página valida por uma definição e o servidor por outra.
 */
export const getAffiliateApplicationFields =
  async (): Promise<AffiliateApplicationFieldsResult> => {
    try {
      const data = await sdk.client.fetch<unknown>(APPLICATION_FORM_PATH, {
        method: "GET",
        cache: "no-store",
      })
      const fields = parseApplicationFormFields(data)
      if (fields === null) {
        // Payload fora do contrato ⇒ erro honesto, nunca um formulário meio-formado.
        console.error(
          "[affiliate-application] GET /store/affiliate-application-form devolveu um payload fora do contrato:",
          data,
        )
        return { ok: false, error: FIELDS_ERROR_MESSAGE }
      }
      return { ok: true, fields }
    } catch (error) {
      // Erro NUNCA silenciado (regra do projeto): loga a causa antes de degradar.
      console.error("[affiliate-application] falha ao ler a definição do formulário:", error)
      return { ok: false, error: FIELDS_ERROR_MESSAGE }
    }
  }

/**
 * Submete a candidatura. `values` já vem aparado e sem chaves fora da definição
 * (`toApplicationSubmission`) — o Engine tem schema FECHADO e recusaria o resto com 400.
 *
 * NUNCA envia `customer_id` nem `status`: o vínculo com o cliente é resolvido pelo Engine a partir do
 * token e o estado nasce `pendente` no workflow. Mandá-los no corpo daria 400 (é o que fecha a
 * injeção).
 */
export const submitAffiliateApplication = async (
  values: ApplicationFormValues,
): Promise<SubmitAffiliateApplicationResult> => {
  const headers = { ...(await getAuthHeaders()) }
  try {
    const data = await sdk.client.fetch<{ id?: unknown }>(APPLICATIONS_PATH, {
      method: "POST",
      body: values,
      headers,
      cache: "no-store",
    })
    if (typeof data?.id !== "string") {
      console.error(
        "[affiliate-application] POST /store/affiliate-applications devolveu uma resposta sem id:",
        data,
      )
      return { ok: false, error: SUBMIT_ERROR_MESSAGE }
    }
    return { ok: true, id: data.id }
  } catch (error) {
    // O SDK lança `FetchError` (com `.status`) em não-2xx. Num 400, o `.message` é a mensagem PT-BR do
    // validador AUTORITÁRIO do Engine — mostramo-la tal e qual, porque diz exatamente qual campo
    // recusou. Só o resto (5xx/rede) vira a mensagem genérica.
    //
    // Isto DEPENDE de a rota devolver a mensagem na chave `message`: o SDK faz
    // `new FetchError(jsonError.message ?? resp.statusText, …)`, logo um corpo só com `{error}`
    // chegaria aqui como `"Bad Request"` — inglês, e sem dizer que campo falhou. A rota manda `error` E
    // `message` de propósito (ver `apps/backend/src/api/store/affiliate-applications/route.ts`); se um
    // dia alguém tirar o `message` de lá, é AQUI que o utilizador paga, e em silêncio.
    const status = (error as { status?: number }).status
    if (status === HTTP_BAD_REQUEST) {
      const message = (error as { message?: unknown }).message
      return {
        ok: false,
        error: typeof message === "string" && message.length > 0 ? message : SUBMIT_ERROR_MESSAGE,
      }
    }
    console.error("[affiliate-application] falha ao submeter a candidatura:", error)
    return { ok: false, error: SUBMIT_ERROR_MESSAGE }
  }
}
